import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { useBusinessTypes } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Shield, Users as UsersIcon } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const ROLES = [{v:"admin",l:"Admin (Owner Workspace)"},{v:"finance",l:"Finance"},{v:"super_admin",l:"Super Admin"}];

const ALL_MENUS = [
  {k:"dashboard",l:"Dashboard"},
  {k:"pemasukan",l:"Daftar Pemasukan"},{k:"kategori_pemasukan",l:"Kategori Pemasukan"},
  {k:"pembeli",l:"Master Pembeli"},
  {k:"pengeluaran",l:"Daftar Pengeluaran"},{k:"kategori_pengeluaran",l:"Kategori Pengeluaran"},
  {k:"laporan_semua",l:"Semua Laporan"},{k:"laba_rugi",l:"Laba Rugi"},
  {k:"neraca",l:"Neraca"},{k:"arus_kas",l:"Arus Kas"},
  {k:"dividen",l:"Pembagian Dividen"},
  {k:"hutang_piutang",l:"Utang & Piutang"},
  {k:"pph_05",l:"PPh 0,5%"},{k:"pph_normal",l:"PPh Normal 22%"},
  {k:"produksi",l:"Produksi (Arang)"},
  {k:"pengaturan",l:"Pengaturan Umum"},
];

export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const bizTypes = useBusinessTypes();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [assignMode, setAssignMode] = useState("existing"); // existing | new
  const empty = {name:"", email:"", no_telp:"", password:"", confirm:"",
                 role:"admin", workspace_id:"", workspace_type:"hostnesia", workspace_name:"", module_access:["keuangan"]};
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [u, w] = await Promise.all([api.get("/users"), api.get("/workspaces")]);
    setUsers(u.data);
    setWorkspaces(w.data);
  };
  useEffect(()=>{ load(); },[]);

  const upd = (k) => (e) => setForm({...form, [k]: e?.target ? e.target.value : e});

  const save = async () => {
    if (form.password !== form.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try {
      const { confirm, ...rest } = form;
      const payload = { ...rest };
      if (rest.role === "super_admin") {
        payload.workspace_id = null; payload.workspace_type = null; payload.workspace_name = null;
      } else if (assignMode === "existing") {
        payload.workspace_type = undefined; payload.workspace_name = undefined;
      } else {
        payload.workspace_id = undefined;
      }
      await api.post("/users", payload);
      setOpen(false); setForm(empty); load(); toast.success("User dibuat");
    } catch(e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Terhapus"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (u) => {
    const s = u.status === "active" ? "inactive" : "active";
    await api.put(`/users/${u.id}`, {status: s}); load();
  };

  const toggleModule = async (u, mod) => {
    const cur = u.module_access || ["keuangan"];
    const next = cur.includes(mod) ? cur.filter((m) => m !== mod) : [...cur, mod];
    if (!next.length) { toast.error("Minimal satu modul harus aktif"); return; }
    try { await api.put(`/users/${u.id}/modules`, { module_access: next }); load(); toast.success("Akses modul diperbarui"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}/role`, {role}); load(); toast.success("Peran diubah"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const savePerms = async () => {
    try {
      await api.put(`/users/${permUser.id}/permissions`, {menu_permissions: permUser.menu_permissions || []});
      toast.success("Permission disimpan"); setPermUser(null); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };

  const delWorkspace = async (w) => {
    if (!window.confirm(`Hapus workspace "${w.name}"? Semua data (user, pemasukan, pengeluaran, produksi, dll) di workspace ini akan ikut terhapus permanen.`)) return;
    try { await api.delete(`/workspaces/${w.id}`); toast.success("Workspace terhapus"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const approveUser = async (u, approve) => {
    try { await api.post(`/users/${u.id}/approve`, { approve });
      toast.success(approve ? "User disetujui" : "User ditolak"); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const togglePerm = (k) => {
    const p = new Set(permUser.menu_permissions || []);
    p.has(k) ? p.delete(k) : p.add(k);
    setPermUser({...permUser, menu_permissions: Array.from(p)});
  };

  const wsLabel = (id) => {
    if (!id) return <span className="text-slate-400 italic">Global</span>;
    const w = workspaces.find(x => x.id === id);
    return w ? `${w.name} (${w.type})` : id;
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(users, 10);

  return (<>
    <PageHeader title="Manajemen User (Super Admin)" subtitle="Kelola semua user & workspace di seluruh sistem."
      actions={<Button onClick={()=>setOpen(true)} className="brand-btn rounded-sm" data-testid="add-user"><Plus size={14} className="mr-1"/>Tambah User</Button>}/>
    <div className="p-4 md:p-8 space-y-4">

      <div className="grid md:grid-cols-3 gap-3">
        <div className="card-flat p-4"><div className="text-xs text-slate-500">Total User</div><div className="text-2xl font-semibold">{users.length}</div></div>
        <div className="card-flat p-4"><div className="text-xs text-slate-500">Total Workspace</div><div className="text-2xl font-semibold">{workspaces.length}</div></div>
        <div className="card-flat p-4"><div className="text-xs text-slate-500">Super Admin</div><div className="text-2xl font-semibold">{users.filter(u=>u.role==="super_admin").length}</div></div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sa-users-table">
          <thead>
            <tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Peran</th>
              <th className="p-3">Workspace</th><th className="p-3">Akses Modul</th><th className="p-3">Status</th><th className="p-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.length===0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Belum ada user.</td></tr>}
            {paged.map(u => (
              <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`sa-user-row-${u.email}`}>
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v)=>changeRole(u, v)}>
                    <SelectTrigger className="h-8 w-40"><SelectValue/></SelectTrigger>
                    <SelectContent>{ROLES.map(r=><SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-[12px]">{wsLabel(u.workspace_id)}</td>
                <td className="p-3">
                  {u.role === "super_admin" ? <span className="text-[11px] text-slate-400">Semua modul</span> : (
                    <div className="flex flex-wrap gap-1" data-testid={`sa-modules-${u.email}`}>
                      {[["keuangan","Keuangan"],["hpp","System HPP"]].map(([k,l]) => {
                        const on = (u.module_access || ["keuangan"]).includes(k);
                        return <button key={k} onClick={()=>toggleModule(u,k)} data-testid={`sa-module-${k}-${u.email}`}
                          className={`text-[11px] px-2 py-0.5 rounded-sm border ${on ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-400 line-through"}`}>{l}</button>;
                      })}
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <button onClick={()=>toggleStatus(u)}
                    className={`text-[11px] px-2 py-0.5 rounded-sm ${
                      u.status==="active" ? "bg-emerald-100 text-emerald-700" :
                      u.status==="pending_verification" ? "bg-amber-100 text-amber-700" :
                      u.status==="pending_approval" ? "bg-blue-100 text-blue-700" :
                      "bg-rose-100 text-rose-700"
                    }`}>
                    {u.status || "active"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {u.status === "pending_approval" && (
                      <button onClick={()=>approveUser(u,true)} className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`sa-approve-${u.email}`}>
                        Setujui
                      </button>
                    )}
                    {u.role !== "super_admin" && (
                      <button onClick={()=>setPermUser({...u, menu_permissions: u.menu_permissions || []})}
                        className="p-1.5 hover:bg-slate-100 rounded-sm" title="Atur Menu">
                        <Shield size={14} className="brand-text"/>
                      </button>
                    )}
                    <button onClick={()=>del(u.id)} className="p-1.5 hover:bg-rose-50 rounded-sm" data-testid={`sa-del-${u.email}`}>
                      <Trash2 size={14} className="text-rose-600"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="sa-users-pagination"/>
      </div>

      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2"><UsersIcon size={14} className="brand-text"/><h3 className="font-semibold text-sm">Workspaces</h3></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {workspaces.map(w => (
            <div key={w.id} className="border rounded-sm p-3 text-sm flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold truncate">{w.name}</div>
                <div className="text-[11px] text-slate-500">Type: {w.type}</div>
                <div className="text-[11px] text-slate-400 font-mono truncate">{w.id}</div>
              </div>
              {w.id !== "ws-hostnesia-default" && (
                <button onClick={()=>delWorkspace(w)} className="p-1.5 rounded-sm hover:bg-rose-50 shrink-0" title="Hapus workspace" data-testid={`sa-del-ws-${w.id}`}>
                  <Trash2 size={14} className="text-rose-600"/>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Create user dialog */}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Tambah User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={upd("name")} data-testid="sa-form-name"/></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")} data-testid="sa-form-email"/></div>
          <div><Label>No. Telp</Label><Input value={form.no_telp} onChange={upd("no_telp")}/></div>
          <div>
            <Label>Akses Modul</Label>
            <div className="flex gap-2 mt-1.5">
              {[["keuangan","Keuangan"],["hpp","System HPP"]].map(([k,l]) => {
                const on = (form.module_access || []).includes(k);
                return <button type="button" key={k} data-testid={`sa-new-module-${k}`}
                  onClick={()=>{ const cur = form.module_access || []; const nx = on ? cur.filter(m=>m!==k) : [...cur, k]; setForm({...form, module_access: nx.length ? nx : cur}); }}
                  className={`px-3 py-1.5 text-xs rounded-sm border ${on ? "brand-btn border-transparent" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>;
              })}
            </div>
          </div>
          <div>
            <Label>Peran</Label>
            <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
              <SelectTrigger data-testid="sa-form-role"><SelectValue/></SelectTrigger>
              <SelectContent>{ROLES.map(r=><SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {form.role !== "super_admin" && (
            <div className="border rounded-sm p-3 space-y-3 bg-slate-50/60">
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-2"><input type="radio" checked={assignMode==="existing"} onChange={()=>setAssignMode("existing")}/> Gabungkan ke workspace</label>
                <label className="flex items-center gap-2"><input type="radio" checked={assignMode==="new"} onChange={()=>setAssignMode("new")}/> Buat workspace baru</label>
              </div>
              {assignMode === "existing" ? (
                <div>
                  <Label>Pilih Workspace</Label>
                  <Select value={form.workspace_id} onValueChange={(v)=>setForm({...form, workspace_id: v})}>
                    <SelectTrigger data-testid="sa-form-ws"><SelectValue placeholder="Pilih..."/></SelectTrigger>
                    <SelectContent>{workspaces.map(w => {
                      const bt = bizTypes.find(x => x.code === w.type);
                      return <SelectItem key={w.id} value={w.id}>{w.name} ({bt?.label || w.type})</SelectItem>;
                    })}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>Jenis Bisnis</Label>
                    <Select value={form.workspace_type} onValueChange={(v)=>setForm({...form, workspace_type: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {bizTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Nama Workspace</Label><Input value={form.workspace_name} onChange={upd("workspace_name")} placeholder="mis. Cabang Malang"/></div>
                </div>
              )}
            </div>
          )}

          <div>
            <Label>Password</Label>
            <div className="relative">
              <Input type={show?"text":"password"} value={form.password} onChange={upd("password")} data-testid="sa-form-password"/>
              <button type="button" onClick={()=>setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">{show?<EyeOff size={16}/>:<Eye size={16}/>}</button>
            </div>
          </div>
          <div><Label>Konfirmasi Password</Label><Input type={show?"text":"password"} value={form.confirm} onChange={upd("confirm")}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
          <Button onClick={save} className="brand-btn" data-testid="sa-form-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Permissions dialog */}
    <Dialog open={!!permUser} onOpenChange={(v)=>!v && setPermUser(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Permission Menu — {permUser?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Centang menu yang boleh diakses. Kosong = akses default (semua menu workspace).</p>
        <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
          {ALL_MENUS.map(m => (
            <label key={m.k} className="flex items-center gap-2 p-2 border rounded-sm hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={(permUser?.menu_permissions || []).includes(m.k)} onCheckedChange={()=>togglePerm(m.k)}/>
              <span className="text-sm">{m.l}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setPermUser(null)}>Batal</Button>
          <Button onClick={savePerms} className="brand-btn">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
